import { Link } from 'react-router-dom';

interface WhyBullet {
  icon: string;
  title: string;
  desc: string;
}

interface SharedWhyChooseUsProps {
  title?: string;
  subtitle?: string;
  bullets?: WhyBullet[];
  imageSrc?: string;
  imageAlt?: string;
  ctaText?: string;
  ctaHref?: string;
}

const DEFAULT_BULLETS: WhyBullet[] = [
  { icon: 'ri-refresh-line', title: 'Reliable & Consistent', desc: 'Same trained crew on every visit. You always know who\'s coming and what to expect — no surprises.' },
  { icon: 'ri-clipboard-line', title: 'Customized Cleaning Plans', desc: 'No cookie-cutter contracts. Every plan is tailored to your facility size, industry, and schedule.' },
  { icon: 'ri-user-star-line', title: 'Trained Professional Staff', desc: 'All crew members are background-checked, uniformed, trained, and GBAC-certified in proper protocols.' },
  { icon: 'ri-time-line', title: 'Flexible Scheduling', desc: 'Early morning, evenings, or weekends — we work around your business hours, not the other way around.' },
  { icon: 'ri-tools-line', title: 'Commercial-Grade Equipment', desc: 'We use professional-grade, EPA-approved cleaning products and equipment for superior results every time.' },
];

export default function SharedWhyChooseUs({
  title = 'Why Businesses Trust OJS Janitorial',
  subtitle = 'Over 20 years of consistent excellence — delivering clean, safe, and professional environments for businesses of all sizes.',
  bullets = DEFAULT_BULLETS,
  imageSrc = 'https://public.readdy.ai/ai/img_res/cf1f76e2b9254f7011eb8e9b50971500.jpg',
  imageAlt = 'OJS Janitorial professional cleaning team',
  ctaText = 'Get a Free Quote',
  ctaHref = '/contact',
}: SharedWhyChooseUsProps) {
  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div className="relative hidden lg:block">
            <div className="relative rounded-3xl overflow-hidden w-full h-[560px]">
              <img
                src={imageSrc}
                alt={imageAlt}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-5 border border-gray-100">
              <div className="flex items-center gap-3 mb-2">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-accent text-sm"></i>
                ))}
              </div>
              <p className="text-navy font-heading font-bold text-sm">5.0 Star Rating</p>
              <p className="text-gray-400 font-body text-xs">Google Reviews — Metairie, LA</p>
            </div>
            {/* Years badge */}
            <div className="absolute -top-6 -left-6 bg-accent rounded-2xl p-5 text-center">
              <div className="text-navy font-heading font-extrabold text-3xl">20+</div>
              <div className="text-navy font-body text-xs font-medium">Years in Business</div>
            </div>
          </div>

          {/* Content */}
          <div>
            <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/20 rounded-full px-4 py-1.5 mb-5">
              <span className="text-accent text-xs font-body font-semibold uppercase tracking-wider">Why Choose Us</span>
            </div>
            <h2 className="font-heading font-extrabold text-navy text-4xl xl:text-5xl mb-5 leading-tight">{title}</h2>
            <p className="text-gray-500 font-body text-lg leading-relaxed mb-10">{subtitle}</p>

            <div className="space-y-5">
              {bullets.map((item, i) => (
                <div key={i} className="flex items-start gap-4 group">
                  <div className="w-11 h-11 flex items-center justify-center bg-accent/10 rounded-xl shrink-0 group-hover:bg-accent transition-colors duration-300">
                    <i className={`${item.icon} text-accent group-hover:text-navy text-base transition-colors duration-300`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-navy text-base mb-1">{item.title}</h3>
                    <p className="text-gray-500 font-body text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <Link
                to={ctaHref}
                className="inline-flex items-center gap-2 bg-accent hover:bg-accent-dark text-navy font-heading font-bold px-8 py-3.5 rounded-full transition-all duration-200 whitespace-nowrap"
              >
                {ctaText} <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
